import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  getProduction,
  updateProduction,
  listVoices,
  listEpisodesByProduction,
  createJob,
} from '../api';
import type { PageOutletContext } from '../components/PageLayout';
import StatusBadge from '../components/StatusBadge';
import type { Production, Voice, AudioJob } from '../types/db';
import {
  cronToScheduleUI,
  scheduleUIToCron,
  getTimeOptions,
  frequencyLabel,
  type FrequencyOption,
} from '../utils/scheduleUtils';

const TABS = [
  { key: 'identity', label: 'Identità' },
  { key: 'source', label: 'Testata' },
  { key: 'voice', label: 'Voce' },
  { key: 'output', label: 'Output' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'editions', label: 'Edizioni' },
];

const PLACEHOLDER_MAP: Record<string, string> = {
  TERRITORIO: 'Bergamo',
  ORA: 'sette',
  GIORNO_SETTIMANA: 'domenica',
  DATA: 'primo giugno',
};

function renderPreview(template: string, brandName: string): string {
  return template
    .replace(/{TERRITORIO}/g, PLACEHOLDER_MAP.TERRITORIO)
    .replace(/{ORA}/g, PLACEHOLDER_MAP.ORA)
    .replace(/{GIORNO_SETTIMANA}/g, PLACEHOLDER_MAP.GIORNO_SETTIMANA)
    .replace(/{DATA}/g, PLACEHOLDER_MAP.DATA)
    .replace(/{BRAND_NAME}/g, brandName || 'VoiceKit');
}

export default function ProductionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { credits, refreshCredits } = useOutletContext<PageOutletContext>();
  const [prod, setProd] = useState<Production | null>(null);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [editions, setEditions] = useState<AudioJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('identity');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  // Schedule local UI state
  const [freq, setFreq] = useState<FrequencyOption>('manual');
  const [time, setTime] = useState('07:00');
  const [customCron, setCustomCron] = useState('');

  const loadData = useCallback(() => {
    if (!id) return;
    getProduction(id)
      .then(async (p) => {
        setProd(p);
        if (p) {
          const eps = await listEpisodesByProduction(p.id, 20);
          setEditions(eps);
          const ui = cronToScheduleUI(p.schedule_cron);
          setFreq(ui.frequency);
          setTime(ui.time);
          setCustomCron(ui.customCron);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadData();
    listVoices('').then(setVoices);
  }, [id, loadData]);

  const openingPreview = useMemo(
    () => renderPreview(prod?.opening_template ?? '', prod?.brand_name ?? ''),
    [prod?.opening_template, prod?.brand_name],
  );
  const closingPreview = useMemo(
    () => renderPreview(prod?.closing_template ?? '', prod?.brand_name ?? ''),
    [prod?.closing_template, prod?.brand_name],
  );

  const handleUpdate = async (partial: Partial<Omit<Production, 'id' | 'created_at'>>) => {
    if (!id || !prod) return;
    setError('');
    setSaved(false);
    try {
      const updated = await updateProduction(id, partial);
      setProd(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  const handleGenerate = async () => {
    if (!id || !prod) return;
    setGenerating(true);
    try {
      const job = await createJob(id);
      // Sprint Pricing v0: dopo la generazione, refresh dei crediti nel layout
      await refreshCredits();
      navigate(`/dashboard/job/${job.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione');
      setGenerating(false);
    }
  };

  const saveSchedule = async () => {
    const cron = scheduleUIToCron({ frequency: freq, time, customCron });
    await handleUpdate({ schedule_cron: cron });
  };

  const generateLabel = useMemo(() => {
    if (!prod) return 'Genera edizione';
    if (!prod.output_audio && !prod.output_vodcast) return 'Genera edizione';
    if (prod.output_audio && !prod.output_vodcast) return 'Genera audio';
    if (!prod.output_audio && prod.output_vodcast) return 'Genera vodcast';
    return 'Genera audio + vodcast';
  }, [prod?.output_audio, prod?.output_vodcast]);

  if (loading) return <p className="text-sm text-slate-500">Caricamento…</p>;
  if (!prod) return <p className="text-sm text-slate-500">Testata non trovata.</p>;

  // Sprint Pricing v0: gate Genera anche su crediti + subscription
  const creditsBlocked =
    credits != null && (credits.is_blocked || credits.subscription_status !== 'active');
  const canGenerate =
    prod.status === 'active' && (prod.output_audio || prod.output_vodcast) && !creditsBlocked;
  const generateBlockReason = creditsBlocked
    ? credits!.subscription_status !== 'active'
      ? `Sottoscrizione ${credits!.subscription_status}: generazioni bloccate.`
      : 'Crediti esauriti per il periodo corrente.'
    : !prod.output_audio && !prod.output_vodcast
      ? 'Attiva almeno un tipo di output'
      : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{prod.name}</h1>
          <p className="mt-1 text-xs text-slate-500">
            {prod.brand_name} · {prod.base_url}
          </p>
        </div>
      </div>

      {error && <p className="text-sm text-rose-600">{error}</p>}
      {saved && <p className="text-sm text-emerald-600">Salvato.</p>}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto border-b border-slate-200 pb-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`whitespace-nowrap rounded-t-lg px-3 py-2 text-sm font-medium transition ${
              activeTab === t.key
                ? 'bg-white text-brand-700 ring-1 ring-slate-200'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Identità ── */}
      {activeTab === 'identity' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Brand</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700">Nome testata</label>
                <input
                  type="text"
                  maxLength={50}
                  defaultValue={prod.name}
                  onBlur={(e) => handleUpdate({ name: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Nome brand</label>
                <input
                  type="text"
                  maxLength={30}
                  defaultValue={prod.brand_name}
                  onBlur={(e) => handleUpdate({ brand_name: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700">Tagline</label>
                <input
                  type="text"
                  maxLength={80}
                  defaultValue={prod.tagline ?? ''}
                  onBlur={(e) => handleUpdate({ tagline: e.target.value || null })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Template</h2>
            <p className="mt-1 text-xs text-slate-500">
              Placeholder:{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-slate-700">
                {'{TERRITORIO} {ORA} {GIORNO_SETTIMANA} {DATA} {BRAND_NAME}'}
              </code>
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Apertura</label>
                <textarea
                  defaultValue={prod.opening_template}
                  onBlur={(e) => handleUpdate({ opening_template: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Chiusura</label>
                <textarea
                  defaultValue={prod.closing_template}
                  onBlur={(e) => handleUpdate({ closing_template: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Anteprima</h2>
            <div className="mt-3 space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase text-slate-500">Apertura</span>
                <p className="mt-1 italic">{openingPreview}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase text-slate-500">Chiusura</span>
                <p className="mt-1 italic">{closingPreview}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Fonte WP ── */}
      {activeTab === 'source' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">Fonte WordPress</h2>
          <div>
            <label className="block text-sm font-medium text-slate-700">Slug</label>
            <input
              type="text"
              defaultValue={prod.slug}
              onBlur={(e) => handleUpdate({ slug: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">URL del sito</label>
            <input
              type="url"
              defaultValue={prod.base_url}
              onBlur={(e) => handleUpdate({ base_url: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Endpoint REST API</label>
            <input
              type="url"
              defaultValue={prod.api_base}
              onBlur={(e) => handleUpdate({ api_base: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-slate-500">
              Normalmente <code>{'{URL}/wp-json/wp/v2'}</code>.
            </p>
          </div>
          <label className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              checked={prod.active}
              onChange={(e) => handleUpdate({ active: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-slate-700">Fonte attiva</span>
          </label>
        </div>
      )}

      {/* ── Voce ── */}
      {activeTab === 'voice' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-700">Voce default</h2>
          <select
            value={prod.default_voice_id ?? ''}
            onChange={(e) => handleUpdate({ default_voice_id: e.target.value || null })}
            className="mt-3 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">Voce libreria (default)</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Output ── */}
      {activeTab === 'output' && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-700">Output</h2>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={prod.output_audio}
              onChange={(e) => handleUpdate({ output_audio: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-slate-700">Genera audio (MP3)</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={prod.output_vodcast}
              onChange={(e) => handleUpdate({ output_vodcast: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-slate-700">Genera vodcast (MP4)</span>
          </label>

          <div className="pt-2">
            <button
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
              title={generateBlockReason}
              className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating ? 'Generazione…' : generateLabel}
            </button>
            {creditsBlocked && <p className="mt-2 text-xs text-rose-600">{generateBlockReason}</p>}
          </div>
        </div>
      )}

      {/* ── Schedule ── */}
      {activeTab === 'schedule' && (
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-700">Schedule</h2>

          <div>
            <label className="block text-sm font-medium text-slate-700">Frequenza</label>
            <select
              value={freq}
              onChange={(e) => setFreq(e.target.value as FrequencyOption)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              {(['manual', 'daily', 'weekdays', 'weekends', 'custom'] as FrequencyOption[]).map(
                (f) => (
                  <option key={f} value={f}>
                    {frequencyLabel(f)}
                  </option>
                ),
              )}
            </select>
          </div>

          {freq !== 'manual' && freq !== 'custom' && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Orario</label>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {getTimeOptions().map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          {freq === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Cron avanzato</label>
              <input
                type="text"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                placeholder="0 7 * * *"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700">Timezone</label>
            <select
              value={prod.schedule_timezone}
              onChange={(e) => handleUpdate({ schedule_timezone: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="Europe/Rome">Europe/Rome</option>
              <option value="Europe/Paris">Europe/Paris</option>
              <option value="Europe/London">Europe/London</option>
            </select>
          </div>

          <button
            onClick={saveSchedule}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            Salva schedule
          </button>

          <p className="text-xs text-slate-500">
            Lo scheduler automatico verrà attivato in uno sprint dedicato. Per ora le edizioni vanno
            avviate manualmente dalla tab Output.
          </p>
        </div>
      )}

      {/* ── Edizioni (audio_jobs) ── */}
      {activeTab === 'editions' && (
        <div className="space-y-4">
          {editions.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna edizione ancora.</p>
          ) : (
            editions.map((ep) => (
              <Link
                key={ep.id}
                to={`/dashboard/job/${ep.id}`}
                className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-brand-500 hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">
                        Edizione #{ep.id.slice(-4)}
                      </p>
                      {ep.script_text_edited && (
                        <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                          Modificata
                        </span>
                      )}
                      {ep.regeneration_count > 0 && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Rig. {ep.regeneration_count}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {new Date(ep.created_at).toLocaleString('it-IT', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                  <StatusBadge status={ep.status} />
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
